import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Flower2, ArrowRight, X, LogIn, MapPin, Phone, Sparkles,
  ChevronLeft, ChevronRight, Facebook, Instagram, MessageCircle, ShoppingBag,
} from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import axios from "axios";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const API_PUBLIC = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;
const HERO_FALLBACK = "https://images.unsplash.com/photo-1652180126225-403b102484b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxmbG9yaXN0JTIwc2hvcCUyMGludGVyaW9yfGVufDB8fHx8MTc4MDI4ODU1N3ww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showOrder, setShowOrder] = useState(false);

  useEffect(() => {
    axios.get(`${API_PUBLIC}/public/landing`).then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (user) return <Navigate to="/" replace />;

  const biz = data?.business || {};
  const social = data?.social || {};
  const promotions = data?.promotions || [];
  const services = data?.services || [];
  const products = data?.products || [];
  const hero = biz.hero_image || HERO_FALLBACK;
  const waPhone = (social.whatsapp || "").replace(/\D/g, "");
  const waLink = (msg) => waPhone ? `https://wa.me/${waPhone.startsWith("593") ? waPhone : `593${waPhone.replace(/^0/, "")}`}${msg ? `?text=${encodeURIComponent(msg)}` : ""}` : null;

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#2A3F35]" data-testid="landing-page">
      {/* --- Top nav --- */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FAFAF8]/85 border-b border-[#E2E0D8]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#2A3F35] grid place-items-center">
              <Flower2 className="w-5 h-5 text-[#E4D5C7]" strokeWidth={1.7} />
            </div>
            <div className="leading-tight">
              <p className="font-heading text-base font-extrabold">{biz.trade_name || "Floristería"}</p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#8A9A92]">Arreglos florales · Ecuador</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {waLink() && (
              <a data-testid="landing-nav-order" href={waLink()} target="_blank" rel="noreferrer"
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-[#507A59] hover:bg-[#E4EFE6]">
                <MessageCircle className="w-4 h-4" /> Pedido directo
              </a>
            )}
            <button data-testid="landing-login-button" onClick={() => setShowLogin(true)}
              className="btn-primary flex items-center gap-2">
              <LogIn className="w-4 h-4" /> Iniciar sesión
            </button>
          </div>
        </div>
      </header>

      {/* --- Hero --- */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2A3F35]/85 via-[#2A3F35]/55 to-[#2A3F35]/10" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-24 md:py-32 text-[#FAFAF8]">
          <p className="text-xs uppercase tracking-[0.3em] text-[#E4D5C7] mb-5 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Florería artesanal
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] max-w-2xl">
            {biz.tagline || "Arreglos florales que enamoran.\nPídelos online en minutos."}
          </h1>
          <p className="mt-6 max-w-xl text-[#E4D5C7] text-base md:text-lg">
            Bouquets, arreglos corporativos, decoración para eventos y entregas a domicilio en todo Ecuador.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <button data-testid="landing-hero-order" onClick={() => setShowOrder(true)}
              className="bg-[#E4D5C7] text-[#2A3F35] font-bold px-7 py-3.5 rounded-full hover:bg-white transition flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" /> Hacer pedido online
            </button>
            <a href="#promociones" className="px-6 py-3.5 rounded-full border border-[#E4D5C7]/40 hover:border-[#E4D5C7] text-[#FAFAF8] flex items-center gap-2">
              Ver promociones <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* --- Promotions carousel --- */}
      <section id="promociones" className="py-20 lg:py-24 max-w-7xl mx-auto px-5 lg:px-10" data-testid="landing-promotions">
        <SectionHeader
          eyebrow="Ofertas vigentes"
          title="Promociones destacadas"
          subtitle="Aprovecha descuentos especiales por temporada."
        />
        {promotions.length === 0 ? (
          <EmptyHint text="Pronto tendremos nuevas promociones. Síguenos en redes para enterarte primero." />
        ) : (
          <Carousel testid="promotions-carousel">
            {promotions.map((p) => (
              <PromotionCard key={p.id} promo={p} onOrder={() => setShowOrder(true)} />
            ))}
          </Carousel>
        )}
      </section>

      {/* --- Services carousel --- */}
      <section className="py-20 lg:py-24 bg-[#F3F2ED]" data-testid="landing-services">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeader
            eyebrow="Lo que ofrecemos"
            title="Servicios"
            subtitle="Soluciones florales para cada ocasión."
          />
          {services.length === 0 ? (
            <EmptyHint text="Aún no se han publicado servicios. El administrador puede crearlos desde el panel." />
          ) : (
            <Carousel testid="services-carousel">
              {services.map((s) => (
                <ServiceCard key={s.id} service={s} onOrder={() => setShowOrder(true)} />
              ))}
            </Carousel>
          )}
        </div>
      </section>

      {/* --- Contact / order section --- */}
      <section className="py-24 max-w-7xl mx-auto px-5 lg:px-10" data-testid="landing-contact">
        <SectionHeader
          eyebrow="Contáctanos"
          title="Realiza tu pedido"
          subtitle="Estamos a un mensaje de distancia. Elige tu canal favorito."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          <ContactCard
            tint="#E4EFE6" iconColor="#507A59" icon={<MessageCircle className="w-6 h-6" />}
            title="WhatsApp" subtitle={social.whatsapp ? `+${(social.whatsapp || "").replace(/\D/g, "")}` : "Disponible al hacer click"}
            cta="Chatear ahora" onClick={() => setShowOrder(true)}
            disabled={!waPhone} testid="contact-whatsapp"
          />
          <ContactCard
            tint="#DDE6ED" iconColor="#3B5A7A" icon={<Facebook className="w-6 h-6" />}
            title="Facebook" subtitle={social.facebook || "Pronto"}
            cta="Ir a Facebook" href={social.facebook || null} testid="contact-facebook"
          />
          <ContactCard
            tint="#F3DEE5" iconColor="#A4486B" icon={<Instagram className="w-6 h-6" />}
            title="Instagram" subtitle={social.instagram || "Pronto"}
            cta="Ir a Instagram" href={social.instagram || null} testid="contact-instagram"
          />
        </div>
        {(biz.address) && (
          <div className="mt-12 flex items-center gap-3 text-sm text-[#5C6B64] justify-center">
            <MapPin className="w-4 h-4" /> <span>{biz.address}</span>
          </div>
        )}
      </section>

      <footer className="border-t border-[#E2E0D8] py-8 text-center text-xs text-[#8A9A92]">
        <p>{biz.business_name || "Floristería"} · RUC {biz.ruc || ""}</p>
        <p className="mt-1">© {new Date().getFullYear()} · Todos los derechos reservados.</p>
      </footer>

      {/* Floating WhatsApp button */}
      {waPhone && (
        <button onClick={() => setShowOrder(true)} data-testid="landing-fab-whatsapp"
          className="fixed bottom-6 right-6 z-40 bg-[#507A59] hover:bg-[#3F6249] text-white w-14 h-14 rounded-full grid place-items-center shadow-2xl shadow-[#507A59]/40 transition">
          <MessageCircle className="w-7 h-7" />
        </button>
      )}

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      {showOrder && (
        <OrderModal
          products={products}
          waLink={waLink}
          waPhone={waPhone}
          businessName={biz.trade_name || biz.business_name || "Floristería"}
          onClose={() => setShowOrder(false)}
        />
      )}
    </div>
  );
}

/* ---------- helpers ---------- */
function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="max-w-2xl">
      <p className="text-xs uppercase tracking-[0.3em] text-[#8A9A92] mb-3">{eyebrow}</p>
      <h2 className="font-heading text-3xl sm:text-4xl font-black leading-tight">{title}</h2>
      {subtitle && <p className="text-[#5C6B64] mt-3 text-base">{subtitle}</p>}
    </div>
  );
}

function EmptyHint({ text }) {
  return (
    <div className="mt-10 rounded-2xl border border-dashed border-[#D8D4C8] p-10 text-center text-[#8A9A92]">
      {text}
    </div>
  );
}

function Carousel({ children, testid }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", loop: false, dragFree: true });
  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();
  return (
    <div className="relative mt-10" data-testid={testid}>
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-5">
          {children}
        </div>
      </div>
      <button onClick={scrollPrev} aria-label="Anterior" data-testid={`${testid}-prev`}
        className="absolute -left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[#E2E0D8] grid place-items-center shadow-md hover:bg-[#F3F2ED] hidden md:grid">
        <ChevronLeft className="w-5 h-5 text-[#2A3F35]" />
      </button>
      <button onClick={scrollNext} aria-label="Siguiente" data-testid={`${testid}-next`}
        className="absolute -right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[#E2E0D8] grid place-items-center shadow-md hover:bg-[#F3F2ED] hidden md:grid">
        <ChevronRight className="w-5 h-5 text-[#2A3F35]" />
      </button>
    </div>
  );
}

function PromotionCard({ promo, onOrder }) {
  const value = promo.type === "percentage" ? `${promo.value}% OFF` : `$${Number(promo.value).toFixed(2)} OFF`;
  return (
    <article className="flex-[0_0_85%] sm:flex-[0_0_60%] md:flex-[0_0_42%] lg:flex-[0_0_32%] card-soft overflow-hidden group">
      <div className="aspect-[4/3] relative overflow-hidden bg-gradient-to-br from-[#E4D5C7] to-[#F3E8DC]">
        {promo.image_url ? (
          <img src={promo.image_url} alt={promo.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center">
            <Sparkles className="w-12 h-12 text-[#2A3F35]/40" />
          </div>
        )}
        <span className="absolute top-4 left-4 bg-[#2A3F35] text-[#E4D5C7] text-xs font-bold px-3 py-1.5 rounded-full">
          {value}
        </span>
      </div>
      <div className="p-6">
        <h3 className="font-heading text-xl font-extrabold mb-2 line-clamp-1">{promo.name}</h3>
        {promo.description && <p className="text-sm text-[#5C6B64] line-clamp-2">{promo.description}</p>}
        <div className="text-xs text-[#8A9A92] mt-3">
          Válido hasta {new Date(promo.end_date).toLocaleDateString()}
        </div>
        <button onClick={onOrder} className="mt-5 w-full btn-secondary flex items-center justify-center gap-2">
          <MessageCircle className="w-4 h-4" /> Pedir esta promoción
        </button>
      </div>
    </article>
  );
}

function ServiceCard({ service, onOrder }) {
  return (
    <article className="flex-[0_0_85%] sm:flex-[0_0_60%] md:flex-[0_0_42%] lg:flex-[0_0_32%] bg-white rounded-2xl overflow-hidden border border-[#E2E0D8] group">
      <div className="aspect-[4/3] relative overflow-hidden bg-gradient-to-br from-[#DDE6ED] to-[#E4EFE6]">
        {service.image_url ? (
          <img src={service.image_url} alt={service.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center">
            <Flower2 className="w-12 h-12 text-[#2A3F35]/40" />
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="font-heading text-xl font-extrabold mb-2 line-clamp-1">{service.name}</h3>
        {service.description && <p className="text-sm text-[#5C6B64] line-clamp-3">{service.description}</p>}
        <div className="mt-4 flex items-center justify-between">
          {service.price_from != null && service.price_from > 0 ? (
            <p className="text-sm text-[#2A3F35]"><span className="text-[#8A9A92]">Desde</span> <span className="font-bold">$ {Number(service.price_from).toFixed(2)}</span></p>
          ) : <span />}
          <button onClick={onOrder} className="text-sm font-medium text-[#507A59] hover:underline flex items-center gap-1">
            Cotizar <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </article>
  );
}

function ContactCard({ tint, iconColor, icon, title, subtitle, cta, href, onClick, disabled, testid }) {
  const baseClass = "block p-7 rounded-2xl border border-[#E2E0D8] hover:shadow-lg transition group";
  const inner = (
    <>
      <div className="w-12 h-12 rounded-xl grid place-items-center mb-5" style={{ background: tint, color: iconColor }}>
        {icon}
      </div>
      <p className="text-xs uppercase tracking-[0.2em] text-[#8A9A92] mb-1">{title}</p>
      <p className="font-heading font-bold text-[#2A3F35] truncate">{subtitle}</p>
      <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#2A3F35] group-hover:gap-2 transition-all">
        {cta} <ArrowRight className="w-4 h-4" />
      </span>
    </>
  );
  if (disabled) {
    return <div className={`${baseClass} opacity-50 cursor-not-allowed`} data-testid={testid}>{inner}</div>;
  }
  if (href) {
    return <a href={href} target="_blank" rel="noreferrer" className={baseClass} data-testid={testid}>{inner}</a>;
  }
  return <button onClick={onClick} className={`text-left w-full ${baseClass}`} data-testid={testid}>{inner}</button>;
}

/* ---------- Login modal ---------- */
function LoginModal({ onClose }) {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const firstRef = useRef(null);
  useEffect(() => { firstRef.current?.focus(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/55 grid place-items-center p-4" data-testid="login-modal">
      <form onSubmit={submit} data-testid="login-form" className="bg-white rounded-2xl max-w-md w-full p-8 border border-[#E2E0D8] relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#8A9A92] hover:text-[#2A3F35]" data-testid="login-modal-close"><X className="w-5 h-5" /></button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#2A3F35] grid place-items-center">
            <Flower2 className="w-6 h-6 text-[#E4D5C7]" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#8A9A92]">Acceso al sistema</p>
            <h2 className="font-heading text-2xl font-extrabold">Iniciar sesión</h2>
          </div>
        </div>
        <label className="block text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-2">Correo</label>
        <input ref={firstRef} data-testid="login-email-input" type="email" required value={email}
          onChange={(e) => setEmail(e.target.value)} className="input-soft mb-5" placeholder="usuario@correo.com" />
        <label className="block text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-2">Contraseña</label>
        <input data-testid="login-password-input" type="password" required value={password}
          onChange={(e) => setPassword(e.target.value)} className="input-soft" placeholder="••••••••" />
        {error && <p data-testid="login-error" className="mt-4 text-sm text-[#A44A4A]">{error}</p>}
        <button data-testid="login-submit-button" disabled={loading} type="submit"
          className="btn-primary w-full mt-6 flex items-center justify-center gap-2">
          {loading ? "Verificando..." : "Entrar"} {!loading && <ArrowRight className="w-4 h-4" />}
        </button>
        <p className="text-xs text-[#8A9A92] mt-5 text-center">Solo usuarios autorizados.</p>
      </form>
    </div>
  );
}

/* ---------- Online order modal (sends via WhatsApp) ---------- */
function OrderModal({ products, waLink, waPhone, businessName, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", product: "", message: "" });
  const set = (k, v) => setForm({ ...form, [k]: v });

  const compose = () => {
    const lines = [
      `Hola ${businessName}, quisiera realizar un pedido:`,
      `• Nombre: ${form.name || "—"}`,
      `• Teléfono: ${form.phone || "—"}`,
      form.product ? `• Producto / servicio: ${form.product}` : null,
      form.message ? `• Detalles: ${form.message}` : null,
      "",
      "¿Me podrían confirmar disponibilidad y precio? ¡Gracias!",
    ].filter(Boolean);
    return lines.join("\n");
  };

  const send = (e) => {
    e.preventDefault();
    const link = waLink(compose());
    if (link) {
      window.open(link, "_blank", "noopener,noreferrer");
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/55 grid place-items-center p-4" data-testid="order-modal">
      <form onSubmit={send} className="bg-white rounded-2xl max-w-lg w-full p-7 border border-[#E2E0D8] relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#8A9A92] hover:text-[#2A3F35]" data-testid="order-modal-close"><X className="w-5 h-5" /></button>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-[#E4EFE6] grid place-items-center">
            <MessageCircle className="w-6 h-6 text-[#507A59]" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#8A9A92]">Pedido online</p>
            <h2 className="font-heading text-2xl font-extrabold">Cuéntanos qué necesitas</h2>
          </div>
        </div>
        {!waPhone && (
          <p className="bg-[#FFF4E5] text-[#8C5A2B] text-sm p-3 rounded-lg mb-4">
            La floristería aún no ha configurado un número de WhatsApp. Contáctanos por redes sociales mientras tanto.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Tu nombre *">
            <input required data-testid="order-name" className="input-soft" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </Field>
          <Field label="Teléfono *">
            <input required data-testid="order-phone" inputMode="tel" className="input-soft" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Producto o servicio">
              {products.length > 0 ? (
                <select data-testid="order-product" className="input-soft" value={form.product}
                  onChange={(e) => set("product", e.target.value)}>
                  <option value="">Selecciona o describe abajo…</option>
                  {products.map((p) => (
                    <option key={p.id} value={`${p.name} (USD ${Number(p.sale_price).toFixed(2)})`}>
                      {p.name} — $ {Number(p.sale_price).toFixed(2)}
                    </option>
                  ))}
                </select>
              ) : (
                <input data-testid="order-product" className="input-soft" placeholder="Ej. Bouquet de rosas para cumpleaños" value={form.product} onChange={(e) => set("product", e.target.value)} />
              )}
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Detalles del pedido">
              <textarea data-testid="order-message" rows={4} className="input-soft" placeholder="Color, fecha de entrega, dirección…" value={form.message} onChange={(e) => set("message", e.target.value)} />
            </Field>
          </div>
        </div>
        <button data-testid="order-send-button" disabled={!waPhone} type="submit"
          className="btn-primary w-full mt-6 flex items-center justify-center gap-2">
          <MessageCircle className="w-4 h-4" /> Enviar por WhatsApp
        </button>
        <p className="text-xs text-[#8A9A92] mt-4 text-center">Al enviar, se abrirá WhatsApp con tu mensaje pre-llenado.</p>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
